import { useHandler } from "@redotech/react-util/hook";
import { getMaskedOutput, InputMask } from "@redotech/react-util/input-mask";
import classNames from "classnames";
import {
  ForwardedRef,
  forwardRef,
  HTMLInputTypeAttribute,
  memo,
  useEffect,
  useId,
  useState,
} from "react";
import { TextMeasureBox } from "../../common/text-measure-box";
import { SpacingValue } from "../../theme/box";
import { textClasses, TextProps, TextSizeValue } from "../../theme/typography";
import * as redoTextInputCss from "./base-redo-input.module.css";

export const redoInputState = [
  "default",
  "disabled",
  "error",
  "readonly",
  "link",
] as const;
export type RedoInputState = (typeof redoInputState)[number];

export const redoInputSize = ["sm", "md", "lg"] as const;
export type RedoInputSize = (typeof redoInputSize)[number];

export interface BaseRedoInputProps {
  value: string;
  setValue(value: string): void;
  placeholder?: string;
  placeholderStyle?: React.CSSProperties;
  size?: RedoInputSize;
  state?: RedoInputState;
  inputTypeHint?: HTMLInputTypeAttribute;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  required?: boolean;
  maxLength?: number;
  className?: string;
  min?: number;
  max?: number;
  htmlPattern?: string;
  constrainToInputLength?: boolean;
  name?: string;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onPaste?: React.ClipboardEventHandler<HTMLInputElement>;
  mask?: InputMask;
  htmlId?: string;
  ariaLabel?: string;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
  autoCompleteDisabled?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  autoFocus?: boolean;
  step?: number;
  isTextArea?: boolean;
  rows?: number;
  dataTestId?: string;
  onWheel?: React.WheelEventHandler<HTMLInputElement>;
  /** Options to display as autocomplete suggestions via a datalist element */
  datalistOptions?: string[];
}

export const BaseRedoInput = memo(
  forwardRef(function BaseRedoInput(
    {
      size = "sm",
      value,
      placeholder,
      placeholderStyle,
      setValue,
      className,
      inputTypeHint,
      inputMode,
      state = "default",
      required,
      maxLength,
      min,
      max,
      name,
      constrainToInputLength,
      htmlPattern,
      onFocus,
      onBlur,
      onPaste,
      mask,
      htmlId,
      ariaLabel,
      ariaInvalid,
      ariaDescribedBy,
      autoCompleteDisabled,
      onKeyDown,
      onClick,
      autoFocus,
      step,
      dataTestId,
      onWheel,
      datalistOptions,
    }: BaseRedoInputProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) {
    // Needed for readonly styles
    const [textWidth, setTextWidth] = useState<number>(0);
    const [lastMask, setLastMask] = useState<InputMask | undefined>(mask);
    const datalistId = useId();

    const disabled = state === "disabled";
    const readonly = state === "readonly";

    const handleInputContentChange = useHandler(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const realValue = getMaskedOutput({
          mask,
          newInput: event.target.value,
        });
        setValue(realValue);
      },
    );

    useEffect(() => {
      if (mask !== lastMask) {
        setLastMask(mask);
        setValue(getMaskedOutput({ mask, newInput: value }));
      }
    }, [mask, lastMask, setValue, value]);

    const commonProps = {
      "aria-label": ariaLabel,
      "aria-invalid": ariaInvalid,
      "aria-describedby": ariaDescribedBy,
      autoComplete: autoCompleteDisabled ? "off" : undefined,
      autoFocus,
      className: classNames(
        textClasses(sizeToInputTextProps[size]),
        redoTextInputCss.input,
        className,
      ),
      "data-testid": dataTestId,
      disabled: disabled || readonly,
      id: htmlId,
      maxLength,
      name,
      onBlur,
      onChange: handleInputContentChange,
      onClick,
      onFocus,
      onKeyDown,
      onPaste,
      onWheel,
      placeholder,
      required,
      style: {
        ...(readonly || constrainToInputLength
          ? { width: textWidth + "px" }
          : {}),
        ...(state === "link" ? { cursor: "pointer" } : {}),
        ...(placeholderStyle?.color
          ? { "--placeholder-color": placeholderStyle.color }
          : {}),
      },
      value,
    };

    return (
      <>
        <input
          {...commonProps}
          inputMode={inputMode}
          list={datalistOptions ? datalistId : undefined}
          max={max}
          min={min}
          pattern={htmlPattern}
          readOnly={state === "link"}
          ref={ref as ForwardedRef<HTMLInputElement>}
          step={step}
          type={inputTypeHint}
        />
        {datalistOptions && (
          <datalist id={datalistId}>
            {datalistOptions.map((option) => (
              <option key={option} value={option} />
            ))}
          </datalist>
        )}
        <TextMeasureBox
          text={value || placeholder || ""}
          {...sizeToInputTextProps[size]}
          setTextWidth={setTextWidth}
          textWidth={textWidth}
        />
      </>
    );
  }),
);

export const sizeToInputTextProps: Record<RedoInputSize, TextProps> = {
  ["sm"]: { fontSize: "xs" },
  ["md"]: { fontSize: "md" },
  ["lg"]: { fontSize: "md" },
};

export const sizeStyles: Record<RedoInputSize, string> = {
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  ["sm"]: redoTextInputCss.small,
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  ["md"]: redoTextInputCss.medium,
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  ["lg"]: redoTextInputCss.large,
};

export const sizeToDescriptorTextProps: Record<RedoInputSize, TextSizeValue> = {
  ["sm"]: "xs",
  ["md"]: "sm",
  ["lg"]: "sm",
};

export const sizeToPx: Record<RedoInputSize, SpacingValue> = {
  ["sm"]: "md",
  ["md"]: "lg",
  ["lg"]: "xl",
};
